import DataIsReprocessingBar from 'components/ReusableComponents/DataIsReprocessingBar'
import usePlausible from 'hooks/usePlausible'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, useParams } from 'react-router'
import { useToasts } from 'react-toast-notifications'
import notificationsApi from 'services/api/notifications'

import { selectCurrentHotels, selectNotificationPreferences } from '../../accessors/index'
import {
  getNotificationPreferences,
  gotNotificationPreferences
} from '../../actions/notifications'
import Checkbox from '../../components/ReusableComponents/Checkbox/index'
import CustomHeader from '../../components/ReusableComponents/HeaderTitles/index'
import LoadingIndicator from '../../components/ReusableComponents/LoadingIndicator'
import styles from './NotificationPreferences.module.scss'

const NotificationGroup = ({
  notificationName,
  handleChangeAll,
  changeAllLabel,
  children,
  width
}) => (
  <div
    className={[
      styles.noficationGroup,
      width === 'wide' ? styles.noficationGroupWide : styles.notificationGroupNarrow
    ].join(' ')}
  >
    <div className={styles.noficationGroupTitle}>
      {notificationName}
      {handleChangeAll && <button onClick={handleChangeAll}>{changeAllLabel}</button>}
    </div>
    <div>{children}</div>
  </div>
)

const CheckRow = ({ checked, name, handleChange }) => {
  return (
    <label className={styles.hotelRow}>
      <Checkbox checked={checked} onChange={handleChange} noLabel />
      <span className={styles.hotelName}>{name}</span>
    </label>
  )
}

const NotificationPreferences = () => {
  const serverData = useSelector(selectNotificationPreferences)

  const [data, setData] = useState()

  const { groupType } = useParams()

  const currentHotels = useSelector(selectCurrentHotels)

  const dispatch = useDispatch()

  const { addToast } = useToasts()

  let notificationTypes = {
    NegativeReview: 'Low rating reviews',
    WaitingClientChatExists: 'New chat message'
  }

  useEffect(() => {
    dispatch(getNotificationPreferences())
  }, [dispatch])

  useEffect(() => {
    setData(serverData)
  }, [serverData])

  const [isSavingChanges, setSavingChanges] = useState(false)
  const [areChangesSaved, setChangesSaved] = useState()
  const [isSaveDisabled, setSavedDisabled] = useState(true)

  const { postEvent, plausibleEvents } = usePlausible()

  async function putData() {
    postEvent(plausibleEvents.UpdateNotificationConfiguration)

    setSavingChanges(true)

    notificationsApi
      .putPreferences({
        items: data
      })
      .then(res => {
        dispatch(gotNotificationPreferences(res))

        setSavingChanges(false)
        setChangesSaved(true)
        setSavedDisabled(true)
      })
      .catch(() => {
        addToast(`Preferences couldn't be saved`, {
          appearance: 'error'
        })
      })
  }

  function isNotificationOn(notificationType, subsidiaryId) {
    if (!data) {
      return false
    }

    const item = data.find(d => d.kind === notificationType)

    if (item) {
      return item.subsidiaries.includes(subsidiaryId)
    }
  }

  function checkboxChange(e, notificationType, subsidiaryId) {
    let newData = [...data]

    const indexOfNotification = data.findIndex(n => n.kind === notificationType)

    if (e.target.checked) {
      if (indexOfNotification !== -1) {
        newData[indexOfNotification] = {
          ...data[indexOfNotification],
          subsidiaries: [...data[indexOfNotification].subsidiaries, subsidiaryId]
        }
      } else {
        newData = [...newData, { kind: notificationType, subsidiaries: [subsidiaryId] }]
      }
    } else {
      newData[indexOfNotification] = {
        ...data[indexOfNotification],
        subsidiaries: data[indexOfNotification].subsidiaries.filter(
          s => s !== subsidiaryId
        )
      }
    }

    setData(newData)
    setSavedDisabled(false)
  }

  function handleChangeWholeNotificationType(notificationType) {
    let newData

    if (data.find(d => d.kind === notificationType).subsidiaries.length > 0) {
      newData = data.map(d =>
        d.kind === notificationType
          ? {
              kind: notificationType,
              subsidiaries: []
            }
          : d
      )
    } else {
      newData = data.map(d =>
        d.kind === notificationType
          ? {
              kind: notificationType,
              subsidiaries: currentHotels.map(h => h.subsidiaryId)
            }
          : d
      )
    }

    setData(newData)
    setSavedDisabled(false)
  }

  if (!data) {
    return <LoadingIndicator />
  }

  if (!groupType) {
    return (
      <Redirect
        to={
          currentHotels.length > 6
            ? '/notificationPreferences/byType'
            : '/notificationPreferences/byHotel'
        }
      />
    )
  }

  return (
    <div className={styles.wrapper}>
      <CustomHeader title="Configure notifications" width={1000} />
      <DataIsReprocessingBar marginTop={0} marginBottom={0} />
      {/* <Tab.Parent>
        <Tab to="/notificationPreferences/byType">Grouped by type</Tab>
        <Tab to="/notificationPreferences/byHotel">Grouped by hotel</Tab>
      </Tab.Parent> */}

      {groupType === 'byType' ? (
        <div className={styles.content}>
          {Object.keys(notificationTypes).map(k => (
            <NotificationGroup
              width="wide"
              key={k}
              notificationName={notificationTypes[k]}
              handleChangeAll={() => handleChangeWholeNotificationType(k)}
              changeAllLabel={
                data.find(d => d.kind === k).subsidiaries.length > 0
                  ? 'Uncheck all'
                  : 'Check all'
              }
            >
              {currentHotels.map(h => (
                <CheckRow
                  key={h.subsidiaryId}
                  name={h.subsidiaryName}
                  handleChange={e => checkboxChange(e, k, h.subsidiaryId)}
                  checked={isNotificationOn(k, h.subsidiaryId)}
                />
              ))}
            </NotificationGroup>
          ))}
        </div>
      ) : (
        <div className={styles.content}>
          {currentHotels.map(h => (
            <NotificationGroup notificationName={h.subsidiaryName} width="narrow">
              {Object.keys(notificationTypes).map(k => (
                <CheckRow
                  name={notificationTypes[k]}
                  handleChange={e => checkboxChange(e, k, h.subsidiaryId)}
                  checked={isNotificationOn(k, h.subsidiaryId)}
                />
              ))}
            </NotificationGroup>
          ))}
        </div>
      )}
      <div className={styles.footer}>
        <div className={styles.footerContainer}>
          {!isSaveDisabled && !isSavingChanges && (
            <p className={styles.footerUnsavedChanges}>You have unsaved changes</p>
          )}
          {areChangesSaved && isSaveDisabled && !isSavingChanges && (
            <p className={styles.footerSavedChanges}>Changes are saved!</p>
          )}
          <button
            className={styles.footerSaveButton}
            onClick={putData}
            disabled={isSaveDisabled || isSavingChanges}
          >
            {isSavingChanges ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default NotificationPreferences
